/* eslint-disable no-unused-vars */
import WarningTopUp from '@/components/Modal/WarningTopUp';
import Navigation from '@/components/Navigation/Navigation';
import ButtonBackHeader from '@/components/ui/Button/ButtonBackHeader';
import BaseInputFormatNumber from '@/components/ui/Input/BaseInputNumberFormat';
import ParentSubpageShell from '@/components/Parent/ParentSubpageShell';
import UserHeader from '@/components/User/UserHeader';
import { nfComma, nfRp } from '@/constants/nf';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextField } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import * as yup from 'yup';
import { useDropzone } from 'react-dropzone';
import parse from 'html-react-parser';
import { Paid } from '@mui/icons-material';
import usePatch from '@/hooks/request/usePatch';
import { toast } from 'react-toastify';
import { route } from '@/constants/routeName';
import CircleLoader from '@/components/ui/Loader/CircleLoader';
import { getCustomer } from '@/store/slices/authSlice';
import { normalizeOutlet } from '@/helper/parentNormalize';
import { canOrderForChild } from '@/helper/dashboardStatus';

const schema = yup.object().shape({
	nominal: yup
		.number()
		.typeError('Nominal must be a number')
		.required('Nominal is a required field')
		.min(200000, 'Minimum nominal is 200,000'),
});

const nominalModifier = [200000, 300000, 500000, 1000000];

export default function TopUp() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const innerHeight = useSelector((state) => state.content.innerHeight);
	const selectedCustomer = useSelector((state) => state.auth.selectedCustomer);
	const selectedOutlet = useSelector((state) => state.auth.selectedOutlet);
	const outlet = useSelector((state) => state.content.outlet);
	const paymentTransfer = useSelector((state) => state.content.paymentTransfer);
	const accountType = useSelector((state) => state.auth.accountType);
	const isParent = accountType === 'parent';
	const [nominal, setNominal] = useState(null);
	const [step, setStep] = useState(0);
	const [isWarning, setWarning] = useState(false);
	const [file, setFile] = useState(null);

	const customerId = selectedCustomer?.id ?? null;
	const studentName = selectedCustomer?.member?.name;
	const studentClass = selectedCustomer?.member?.on_list_attributes?.class;
	const outletId =
		normalizeOutlet(selectedCustomer?.outlet)?.id ??
		normalizeOutlet(selectedOutlet)?.id ??
		null;

	const {
		handleSubmit,
		setValue,
		trigger,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			nominal: 0,
			file: null,
		},
	});

	useEffect(() => {
		setNominal(null);
		setStep(0);
		setFile(null);
		setWarning(false);
		setValue('nominal', 0);
		setValue('file', null);
	}, [customerId, setValue]);

	const { mutate: createOrderTopUp, isPending } = usePatch({
		url: `point-of-sale/topup_requests`,
		method: 'post',
		type: 'form-data',
		callback: (res) => {
			if (res.status === 201) {
				if (customerId != null) {
					dispatch(getCustomer({ params: {}, id: customerId }));
				}
				queryClient.invalidateQueries({ queryKey: ['parent-dashboard'] });
				queryClient.invalidateQueries({ queryKey: ['my-topup-orders'] });
				queryClient.invalidateQueries({ queryKey: ['my-new-topup-orders'] });
				toast.success(`order created successfully`);
				navigate(`/${route.accountTopUps}/`);
			}
		},
		showError: true,
	});

	useEffect(() => {
		dispatch({ type: 'auth/setSwitchingLocked', payload: isPending });
		return () => {
			dispatch({ type: 'auth/setSwitchingLocked', payload: false });
		};
	}, [isPending, dispatch]);

	const onSubmit = async (data) => {
		if (!selectedCustomer || customerId == null) {
			toast.error('No student selected for this top-up');
			return;
		}
		if (!canOrderForChild(selectedCustomer)) {
			toast.error('This student is inactive for your family. You cannot request a top-up.');
			return;
		}
		if (!data.file || !paymentTransfer || outletId == null) {
			toast.error('Missing payment details or outlet for this student');
			return;
		}

		const fData = new FormData();
		fData.append('amount', data.nominal);
		fData.append('note', '');
		fData.append('customer', String(customerId));
		fData.append('outlet', String(outletId));
		fData.append('payment_type', paymentTransfer);
		fData.append('receipt', data.file);
		await createOrderTopUp(fData);
	};

	const { getRootProps, getInputProps } = useDropzone({
		accept: {
			'image/*': '*',
		},
		multiple: false,
		disabled: isPending || !selectedCustomer,
		onDrop: (dropped) => {
			if (isPending || !selectedCustomer) return;
			setFile(
				Object.assign(dropped[0], {
					preview: URL.createObjectURL(dropped[0]),
				})
			);
			setValue('nominal', +nominal);
			setValue('file', dropped[0]);
			setTimeout(() => {
				handleSubmit(onSubmit)();
			}, 0);
		},
	});

	const onNominalChange = (e) => {
		const val = e.target.value;
		if (val && +val > 0) {
			setValue('nominal', +val);
			trigger('nominal');
			setNominal(val);
		}
	};

	const { mutate: generateUniqueAmount, isPendingGUniqueAmount } = usePatch({
		url: `point-of-sale/topup_requests/generate_unique_amount`,
		method: 'post',
		callback: (res) => {
			if (res.data.unique_amount) {
				setNominal(res.data.unique_amount);
				setValue('nominal', res.data.unique_amount);
				setStep(1);
			}
		},
		onError: () => {
			toast.error('There is an Error when generating top-up amount');
		},
		showError: true,
	});

	const onPay = async () => {
		if (!selectedCustomer || !nominal || +nominal < 200000) {
			return;
		}
		await generateUniqueAmount({ amount: nominal });
	};

	const payDisabled =
		!selectedCustomer || !nominal || +nominal < 200000 || isPendingGUniqueAmount || isPending;

	const warningModal = (
		<WarningTopUp
			open={isWarning}
			onClose={() => setWarning(false)}
			onAction={() => {
				setWarning(false);
				navigate(-1);
			}}
		/>
	);

	const amountField = (
		<>
			<label className={isParent ? 'muted' : undefined} style={{ fontSize: isParent ? 12 : undefined }}>
				Enter the top up amount
			</label>
			<TextField
				name='nominal'
				label=''
				className='w-full'
				size='small'
				value={nominal || 0}
				inputProps={{
					inputMode: 'numeric',
					pattern: '[0-9]*',
					step: '1',
					style: {
						textAlign: 'right',
						fontSize: '20px',
					},
					'aria-label': 'Top up amount',
				}}
				onChange={onNominalChange}
				InputProps={{
					inputComponent: BaseInputFormatNumber,
				}}
				error={!!errors.nominal}
				helperText={errors.nominal?.message}
			/>
			<div className={isParent ? 'topup-amounts' : 'grid grid-cols-2 gap-2'}>
				{nominalModifier.map((v) => (
					<button
						className={twMerge(
							isParent ? undefined : 'btn-payment',
							+nominal === +v ? 'selected' : ''
						)}
						key={v}
						onClick={() => {
							if (+nominal !== +v) {
								setNominal(v);
								setValue('nominal', +v);
							} else {
								setNominal(0);
								setValue('nominal', 0);
							}
							trigger('nominal');
						}}
						type='button'
					>
						{isParent ? nfRp(v) : nfComma(v)}
					</button>
				))}
			</div>
		</>
	);

	if (isParent) {
		return (
			<>
				<ParentSubpageShell
					title='Top up'
					backTo={`/${route.account}`}
					backLabel='← Back to account'
					intro={
						studentName
							? `Top-up for ${studentName}${studentClass ? ` · ${studentClass}` : ''}. Current balance ${nfRp(+selectedCustomer?.balance || 0)}.`
							: 'Select a child from Account before topping up.'
					}
				>
					<form onSubmit={handleSubmit(onSubmit)} className='subpage-form'>
						{step === 0 && (
							<>
								{amountField}
								{outlet?.bank_info && (
									<div className='topup-panel'>{parse(outlet.bank_info)}</div>
								)}
								<div className='subpage-actions'>
									<button
										type='button'
										className='secondary'
										onClick={() => {
											if (step > 0) setWarning(true);
											else navigate(`/${route.account}`);
										}}
									>
										Cancel
									</button>
									<button type='button' className='main' onClick={onPay} disabled={payDisabled}>
										{isPendingGUniqueAmount && <CircleLoader className='me-2' />}
										Pay
									</button>
								</div>
							</>
						)}
						{step === 1 && (
							<>
								{outlet?.bank_info && (
									<div className='topup-panel'>{parse(outlet.bank_info)}</div>
								)}
								{outlet?.qris_file_url && (
									<img
										src={outlet.qris_file_url}
										alt='QRIS'
										style={{ width: '100%', maxWidth: 240, margin: '0 auto', display: 'block' }}
									/>
								)}
								<p>
									Amount to pay:{' '}
									<strong>{nfRp(+nominal || 0)}</strong>
								</p>
								<div {...getRootProps({ className: 'dropzone' })}>
									<input {...getInputProps()} />
									<button
										className='main full'
										type='button'
										disabled={isPending || !selectedCustomer}
										style={{ width: '100%' }}
									>
										{!isPending ? <Paid fontSize='small' /> : <CircleLoader />}
										<span style={{ marginLeft: 8 }}>I already paid</span>
									</button>
									{file && <p className='muted' style={{ marginTop: 8 }}>{file.name}</p>}
								</div>
								<button
									type='button'
									className='secondary'
									style={{ width: '100%' }}
									onClick={() => setWarning(true)}
								>
									Cancel
								</button>
							</>
						)}
					</form>
				</ParentSubpageShell>
				{warningModal}
			</>
		);
	}

	return (
		<div style={{ minHeight: innerHeight }} className='app-container app-page'>
			<div className='flex flex-col w-full h-full gap-4 p-4 pb-24'>
				<UserHeader />
				{studentName && (
					<p className='text-sm text-[var(--weak)]' data-testid='topup-ordering-for'>
						Top-up for <span className='font-bold text-black'>{studentName}</span>
					</p>
				)}
				<div className='flex flex-col flex-1 w-full h-full gap-12'>
					<div className='relative'>
						<ButtonBackHeader
							className='top-0'
							beforeNavigate={() => {
								if (step > 0) {
									return setWarning(true);
								}
								navigate(-1);
							}}
						/>
						<div className='relative flex items-center justify-center'>
							<span className='ml-4 font-bold'>Top-Up</span>
						</div>
					</div>
					<form onSubmit={handleSubmit(onSubmit)} className='relative flex flex-col flex-1 h-full gap-4 pb-4'>
						{step === 0 && (
							<>
								<div className='flex flex-col gap-2'>{amountField}</div>
								{outlet?.bank_info && <div className='flex flex-col gap-2'>{parse(outlet.bank_info)}</div>}
								<div className='grid grid-cols-2 gap-2 mt-auto'>
									<button type='button' className='w-full btn-actions' onClick={() => navigate(-1)}>
										Cancel
									</button>
									<button type='button' className='w-full btn-actions' onClick={onPay} disabled={payDisabled}>
										{isPendingGUniqueAmount && <CircleLoader className='me-2' />}
										Pay
									</button>
								</div>
							</>
						)}
						{step === 1 && (
							<div className='flex flex-col gap-4'>
								{studentName && (
									<p className='text-sm text-[var(--weak)]'>
										Uploading receipt for <span className='font-bold text-black'>{studentName}</span>
									</p>
								)}
								{outlet?.bank_info && <div className='flex flex-col gap-2'>{parse(outlet.bank_info)}</div>}
								{outlet?.qris_file_url && <img src={outlet.qris_file_url} alt='qris' className='w-full m-auto max-w-60' />}
								<div>
									Amount to pay: <span className='font-bold text-huge'>IDR {nfComma(nominal)}</span>
								</div>
								<div {...getRootProps({ className: 'dropzone flex flex-col gap-2 cursor-pointer' })}>
									<div className='flex flex-col items-center justify-center w-full gap-2'>
										<label className='flex flex-col items-center w-full'>
											<input {...getInputProps()} />
											<button
												className='flex items-center w-full gap-2 btn-actions'
												type='button'
												disabled={isPending || !selectedCustomer}
											>
												{!isPending ? <Paid /> : <CircleLoader />}
												<span>I Already Paid</span>
											</button>
										</label>
										{file && <span className='ml-2'>{file.name}</span>}
									</div>
								</div>
							</div>
						)}
					</form>
				</div>
				{warningModal}
			</div>
			<Navigation />
		</div>
	);
}
